// ============================================================================
// KLINISOL CRO - Electronic Signature Modal (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// FDA 21 CFR Part 11 Compliance - For SOP Operations
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState } from 'react';
import { 
    X, 
    PenLine, 
    Eye, 
    EyeOff, 
    AlertCircle, 
    Shield,
    FileCheck,
    Loader2
} from 'lucide-react';

const ESignatureModal = ({ 
    isOpen, 
    onClose, 
    onSign, 
    actionType = 'authorship',
    actionDescription = '',
    loading = false 
}) => {
    const [pin, setPin] = useState('');
    const [comments, setComments] = useState('');
    const [error, setError] = useState('');
    const [showPin, setShowPin] = useState(false);

    // Signature meanings based on action type
    const signatureMeanings = {
        authorship_upload: 'I certify that I am the author/uploader of this document and that the information contained herein is accurate to the best of my knowledge.',
        authorship_template: 'I certify that I created this fillable template from the source SOP and that it accurately represents the original document.',
        approval: 'I approve this Standard Operating Procedure for implementation and use within KLINISOL operations.',
        review: 'I have reviewed this SOP and confirm it is accurate, current, and compliant with applicable regulations.'
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Validate PIN
        if (!pin || pin.trim().length === 0) {
            setError('PIN is required for electronic signature');
            return;
        }

        if (pin.length < 4) {
            setError('PIN must be at least 4 characters');
            return;
        }

        // Get signature meaning
        const signatureMeaning = signatureMeanings[actionType] || signatureMeanings.authorship_upload;

        // Call onSign callback with signature data
        onSign({
            pin: pin.trim(),
            signatureMeaning,
            comments: comments.trim() || null
        });
    };

    const handleClose = () => {
        if (!loading) {
            setPin('');
            setComments('');
            setError('');
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={handleClose}>
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] p-7 rounded-t-3xl flex justify-between items-center text-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <PenLine className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-black">
                            Electronic Signature Required
                        </h2>
                    </div>
                    <button 
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center justify-center"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8">
                    {/* FDA Compliance Notice */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-[#475569] p-5 rounded-xl mb-6">
                        <div className="flex gap-3 items-start">
                            <Shield className="w-6 h-6 text-[#475569] flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold text-[#475569] mb-1">21 CFR Part 11 Compliance</p>
                                <p className="text-sm text-[#475569]">
                                    This action requires your electronic signature to maintain regulatory compliance.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Action Description */}
                    {actionDescription && (
                        <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 mb-6">
                            <div className="flex items-start gap-3">
                                <FileCheck className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" />
                                <div>
                                    <label className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-1">
                                        Action:
                                    </label>
                                    <p className="text-base text-gray-700">{actionDescription}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Signature Meaning */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-5 mb-6">
                        <label className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                            Signature Meaning:
                        </label>
                        <div className="text-sm text-gray-700 leading-relaxed italic">
                            {signatureMeanings[actionType] || signatureMeanings.authorship_upload}
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* PIN Input */}
                        <div>
                            <label htmlFor="pin" className="block text-sm font-bold text-gray-900 mb-2">
                                Enter Your PIN: <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showPin ? 'text' : 'password'}
                                    id="pin"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    placeholder="Enter your PIN or password"
                                    className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    autoFocus
                                    disabled={loading}
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors p-1"
                                    onClick={() => setShowPin(!showPin)}
                                    disabled={loading}
                                >
                                    {showPin ? (
                                        <Eye className="w-5 h-5" />
                                    ) : (
                                        <EyeOff className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-600">
                                Use your account password or PIN
                            </p>
                        </div>

                        {/* Comments (Optional) */}
                        <div>
                            <label htmlFor="comments" className="block text-sm font-bold text-gray-900 mb-2">
                                Comments <span className="text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <textarea
                                id="comments"
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                placeholder="Add any additional comments..."
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 resize-vertical disabled:opacity-50 disabled:cursor-not-allowed"
                                rows="3"
                                disabled={loading}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 p-4 rounded-xl flex gap-3 items-start">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-red-900 leading-relaxed">
                                    {error}
                                </span>
                            </div>
                        )}

                        {/* Agreement */}
                        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
                            <p className="text-sm text-yellow-900 leading-relaxed">
                                By clicking "Sign", I agree that this electronic signature has the same legal effect as a handwritten signature.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 flex-wrap pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 min-w-[150px] px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`flex-[2] min-w-[200px] px-6 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                                    loading
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:-translate-y-0.5 shadow-lg'
                                }`}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Signing...</span>
                                    </>
                                ) : (
                                    <>
                                        <PenLine className="w-5 h-5" />
                                        <span>Sign</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ESignatureModal;